import React, { useCallback, useEffect, useRef, useState } from "react";
import config from "../config";
import "./LightParkDashboard.css";

// The Light Park performance dashboard.
// The dashboard itself is a static page (public/light-park/dashboard.html, built by server/app/light_park/web/build_page.py)
// with no data inside. This page fetches the cached payload (refreshed nightly by cron) with the signed-in user's cookie
// and hands it to that page once it says it's ready.
const LightParkDashboard = () => {
  const frameRef = useRef(null);
  const dataRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [refreshed, setRefreshed] = useState("");
  const [frameKey, setFrameKey] = useState(0);

  const send = useCallback(() => {
    const win = frameRef.current && frameRef.current.contentWindow;
    if (win && dataRef.current) {
      win.postMessage({ type: "light-park-data", data: dataRef.current }, window.location.origin);
    }
  }, []);

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 60000);
    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.lightPark.data}`,
        { method: "GET", credentials: "include", signal: ctrl.signal }
      );
      const json = await response.json();
      if (!response.ok || !json.success) throw new Error(json.error || "Failed to load the dashboard data");
      const nextWhen = json.data.refreshed || "";
      const changed = nextWhen !== dataRef.current?.refreshed;
      dataRef.current = json.data;
      setRefreshed(nextWhen);
      setStatus("ready");
      if (changed) setFrameKey((k) => k + 1);
    } catch (err) {
      console.error("Light Park dashboard:", err);
      setError(
        err.name === "AbortError"
          ? "Timed out loading the dashboard data."
          : err.message
      );
      setStatus("error");
    } finally {
      clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === "light-park-ready") send();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [send]);

  const when = refreshed
    ? new Date(refreshed).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

  let statusText = "";
  if (status === "loading") {
    statusText = "Loading dashboard…";
  } else if (status === "ready") {
    statusText = when ? `Updated ${when}` : "Ready";
  } else if (status === "error") {
    statusText = `Couldn't load the data: ${error}`;
  }

  return (
    <div className="lp-dashboard">
      <div className="lp-bar">
        <span className="lp-status">{statusText}</span>
      </div>
      {status !== "error" && dataRef.current && (
        <iframe
          key={frameKey}
          ref={frameRef}
          className="lp-frame"
          title="The Light Park performance dashboard"
          src={`${process.env.PUBLIC_URL || ""}/light-park/dashboard.html`}
          onLoad={send}
        />
      )}
    </div>
  );
};

export default LightParkDashboard;
